import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { PaymentHistory } from "@/types/payment";
import { ProductTable } from "@/components/product-table";
import { AddProductDialog } from "@/components/add-product-dialog";
import { EditProductDialog } from "@/components/edit-product-dialog";
import { realtimeService, RealtimeEvent } from "@/lib/realtime-service";
import { PaymentConfirmationDialog } from "@/components/ui/payment-confirmation-dialog";
import { PaymentInvoice } from "@/components/payment-invoice";
import { useReactToPrint } from "react-to-print";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  User,
  Phone,
  Briefcase,
  MapPin,
  Plus,
  Package,
  DollarSign,
  Banknote,
  CreditCard,
  CheckCircle,
  Hash,
  Trash2,
  ArrowLeft,
  Shield,
  FileText,
  Smartphone,
  Users,
  Receipt,
  FileBarChart,
  Settings,
  LogOut,
  Info,
  Eraser,
  Printer,
  ChevronDown,
  ChevronUp,
  History,
  LayoutDashboard,
  Bell,
  ShoppingCart,
  Menu,
  X,
  MoreHorizontal,
  Pencil,
  CalendarIcon,
} from "lucide-react";
import { DialogDatePicker } from "@/components/ui/dialog-date-picker";

interface CustomerPaymentSheetProps {
  customer: Customer | null;
  products: Product[];
  open: boolean;
  onClose: () => void;
  onDelete?: (id: string) => void;
  onAddProduct: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  onDeleteProduct: (id: string) => void;
  onUpdateProduct: (product: Product) => void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "settings") => void;
  hideAddProduct?: boolean;
}

export function CustomerPaymentSheet({
  customer,
  products,
  open,
  onClose,
  onDelete,
  onAddProduct,
  onDeleteProduct,
  onUpdateProduct,
  onNavigate,
  hideAddProduct = false,
}: CustomerPaymentSheetProps) {
  const [selectedProductId, setSelectedProductId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [globalPaymentAmount, setGlobalPaymentAmount] = useState("");
  const [globalPaymentDate, setGlobalPaymentDate] = useState<Date>(new Date());
  const [isGlobalPaymentInitialized, setIsGlobalPaymentInitialized] = useState(false);
  const [productPayments, setProductPayments] = useState<Record<string, string>>({});
  const [productPaymentDates, setProductPaymentDates] = useState<Record<string, Date>>({});
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [expandedCompletedProducts, setExpandedCompletedProducts] = useState<Set<string>>(new Set());
  const [paymentCurrency, setPaymentCurrency] = useState<"IQD" | "USD">("IQD");
  const [productInstallments, setProductInstallments] = useState<Record<string, number>>({});
  const [activeTab, setActiveTab] = useState("products");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState<{
    productsToUpdate: Product[];
    paymentsToAdd: PaymentHistory[];
    withPrint: boolean;
  } | null>(null);
  const [selectedInstallmentFilter, setSelectedInstallmentFilter] = useState<number | "all">("all");
  
  // State و Ref بۆ پسوڵەی چاپکردن
  const [invoiceData, setInvoiceData] = useState<{
    payments: PaymentHistory[];
    invoiceNumber: number;
    paymentDate: Date;
  } | null>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);

  // فۆنکشنی چاپکردنی پسوڵە
  const handlePrintInvoice = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: `وەصڵی پارەدان - ${customer?.fullName || ""}`,
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 5mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .no-print {
          display: none !important;
        }
      }
    `,
    onAfterPrint: () => {
      setInvoiceData(null);
    },
  });

  // فۆنکشنی چاپکردنی پارەدان لە مێژوو - بە ژمارەی وەصڵ
  const printPaymentFromHistory = (payment: PaymentHistory) => {
    if (!customer) return;
    
    // دۆزینەوەی هەموو پارەدانەکان کە هەمان ژمارەی وەصڵیان هەیە
    let paymentsToPrint: PaymentHistory[];
    
    if (payment.invoiceNumber && payment.invoiceNumber > 0) {
      // ئەگەر ژمارەی وەصڵ هەیە، هەموو پارەدانەکان بە هەمان ژمارەی وەصڵ بگرە
      paymentsToPrint = paymentHistory.filter(p => p.invoiceNumber === payment.invoiceNumber);
    } else {
      // ئەگەر ژمارەی وەصڵ نییە، تەنها ئەم پارەدانە
      paymentsToPrint = [payment];
    }
    
    // ڕێکخستنی داتای پسوڵە
    setInvoiceData({
      payments: paymentsToPrint,
      invoiceNumber: payment.invoiceNumber || 0,
      paymentDate: new Date(payment.date),
    });
    
    // چاوەڕوانی کەمێک بۆ ڕێندەرکردنی پسوڵە پێش چاپکردن
    setTimeout(() => {
      handlePrintInvoice();
    }, 100);
  };

  // فۆنکشنی دروستکردنی ID
  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };

  // API URL
  const getApiUrl = () => {
    return import.meta.env.VITE_KV_API_URL || 'https://qandil-realtime-api.qandil.workers.dev';
  };

  // خوێندنەوەی مێژووی پارەدان لە داتابەیس
  const fetchPaymentHistory = useCallback(async () => {
    if (!customer) return;
    
    setIsLoadingPayments(true);
    try {
      const token = localStorage.getItem('auth_token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/payments/customer/${customer.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  }, [customer]);

  // زیادکردنی پارەدان بۆ داتابەیس
  const addPaymentToDatabase = async (payment: PaymentHistory): Promise<PaymentHistory | null> => {
    try {
      const token = localStorage.getItem('auth_token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payment),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save payment');
      }
      
      const result = await response.json();
      // گەڕاندنەوەی پارەدان لەگەڵ ژمارەی وەصڵی ڕاستەقینە لە سێرڤەر
      return {
        ...payment,
        invoiceNumber: result.invoiceNumber || payment.invoiceNumber
      };
    } catch (error) {
      console.error('Error saving payment:', error);
      return null;
    }
  };

  // زیادکردنی چەند پارەدان بە یەک ژمارەی وەصڵ
  const addBatchPaymentsToDatabase = async (payments: PaymentHistory[]): Promise<PaymentHistory[]> => {
    try {
      const token = localStorage.getItem('auth_token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/payments/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payments }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save batch payments');
      }
      
      const result = await response.json();
      return result.payments || [];
    } catch (error) {
      console.error('Error saving batch payments:', error);
      return [];
    }
  };

  // سڕینەوەی پارەدان لە داتابەیس
  const deletePaymentFromDatabase = async (paymentId: string) => {
    try {
      const token = localStorage.getItem('auth_token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete payment');
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting payment:', error);
      return false;
    }
  };

  const toggleExpanded = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set<string>();
      // ئەگەر ئەم کارتە کراوە نەبوو، بیکەرەوە (و هەموو ئەوانی تر دابخە)
      if (!prev.has(productId)) {
        newSet.add(productId);
      }
      // ئەگەر کراوە بوو، دایبخە (newSet بەتاڵ دەمێنێتەوە)
      return newSet;
    });
  };

  const toggleCompletedProductExpanded = (productId: string) => {
    setExpandedCompletedProducts(prev => {
      const newSet = new Set<string>();
      if (!prev.has(productId)) {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // فۆنکشنی فۆرماتکردنی ژمارە بە کۆما
  const formatNumberWithCommas = (value: string | number): string => {
    const num = typeof value === 'string' ? value.replace(/,/g, '') : value.toString();
    if (!num || isNaN(Number(num))) return '';
    return Number(num).toLocaleString();
  };

  // فۆنکشنی لابردنی کۆما و گەڕاندنەوەی ژمارەی ڕەسەن
  const parseFormattedNumber = (value: string): string => {
    return value.replace(/,/g, '');
  };

  // ڕێگری لە سکرۆڵی body کاتێک sheet کراوەیە (بۆ مۆبایل)
  useEffect(() => {
    if (open) {
      // هەڵگرتنی scroll position ی ئێستا
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // گەڕاندنەوەی scroll کاتێک sheet دادەخرێت
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  // خوێندنەوەی مێژووی پارەدان لە داتابەیس کاتێک کۆمپۆنێنت لۆد دەبێت
  useEffect(() => {
    if (customer && open) {
      fetchPaymentHistory();
    }
  }, [customer, open, fetchPaymentHistory]);

  // گوێگرتن لە realtime events بۆ پارەدان
  useEffect(() => {
    if (!customer || !open) return;

    const handleRealtimeEvent = (event: RealtimeEvent) => {
      if (event.type === 'payment_added') {
        // ئەگەر پارەدانەکە بۆ ئەم کڕیارەیە، زیادی بکە بە لیست
        if (event.data.customerId === customer.id) {
          setPaymentHistory(prev => {
            // ئەگەر پێشتر هەیە، زیادی مەکە
            if (prev.some(p => p.id === event.data.id)) return prev;
            return [event.data, ...prev];
          });
        }
      } else if (event.type === 'payment_deleted') {
        setPaymentHistory(prev => prev.filter(p => p.id !== event.data.id));
      }
    };

    const unsubscribe = realtimeService.subscribe(handleRealtimeEvent);
    return () => unsubscribe();
  }, [customer, open]);

  if (!customer || !open) return null;

  const handleDelete = () => {
    if (onDelete) {
      const customerProducts = products.filter(p => p.customerId === customer.id);
      if (customerProducts.length > 0) {
        alert("ناتوانرێت ئەم کڕیارە بسڕێتەوە چونکە کاڵای لەسەر تۆمارکراوە");
        return;
      }
      if (confirm("دڵنیایت لە سڕینەوەی ئەم کڕیارە؟")) {
        onDelete(customer.id);
        onClose();
      }
    }
  };

  const handleConfirmPayment = async () => {
    if (!pendingPaymentAction) return;

    const { productsToUpdate, paymentsToAdd, withPrint } = pendingPaymentAction;

    // تۆمارکردن لە داتابەیس - هەموو پارەدانەکان بە یەک ژمارەی وەصڵ
    const savedPayments = await addBatchPaymentsToDatabase(paymentsToAdd);
    
    // تەنها ئەوانە زیاد بکە کە پێشتر نەبوون (بۆ ڕێگری لە duplicate لەگەڵ realtime)
    setPaymentHistory((prev) => {
      const existingIds = new Set(prev.map(p => p.id));
      const newPayments = savedPayments.filter(p => !existingIds.has(p.id));
      return [...newPayments, ...prev];
    });
    
    productsToUpdate.forEach(p => onUpdateProduct(p));
    setProductPayments({});
    setProductInstallments({});
    setGlobalPaymentAmount("");
    setGlobalPaymentDate(new Date());
    setPendingPaymentAction(null);

    if (withPrint && savedPayments.length > 0) {
      // ڕێکخستنی داتای پسوڵە بۆ چاپکردن
      const invoiceNumber = savedPayments[0]?.invoiceNumber || 0;
      setInvoiceData({
        payments: savedPayments,
        invoiceNumber,
        paymentDate: globalPaymentDate,
      });
      
      // چاوەڕوانی کەمێک بۆ ڕێندەرکردنی پسوڵە پێش چاپکردن
      setTimeout(() => {
        handlePrintInvoice();
      }, 100);
    }
  };

  const customerProducts = products.filter((p) => p.customerId === customer.id);
  const activeProducts = customerProducts.filter((p) => p.remainingAmount > 0);
  const completedProducts = customerProducts.filter((p) => p.remainingAmount <= 0);

  const iqdProducts = customerProducts.filter((p) => p.currency === "IQD");
  const totalSaleIQD = iqdProducts.reduce((sum, p) => sum + p.salePrice, 0);
  const totalPaidIQD = iqdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalRemainingIQD = iqdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
  const totalMonthlyIQD = iqdProducts.filter(p => p.remainingAmount > 0).reduce((sum, p) => sum + p.monthlyInstallment, 0);

  const usdProducts = customerProducts.filter((p) => p.currency === "USD");
  const totalSaleUSD = usdProducts.reduce((sum, p) => sum + p.salePrice, 0);
  const totalPaidUSD = usdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalRemainingUSD = usdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
  const totalMonthlyUSD = usdProducts.filter(p => p.remainingAmount > 0).reduce((sum, p) => sum + p.monthlyInstallment, 0);

  const selectedProduct = activeProducts.find((p) => p.id === selectedProductId);

  const handlePayment = async () => {
    if (!selectedProduct || !paymentAmount) return;
    const amount = parseFloat(paymentAmount);
    if (amount <= 0) return;

    const newInstallmentNumber = (selectedProduct.installments || 0) + 1;
    const newPayment: PaymentHistory = {
      id: generateId(),
      customerId: customer.id,
      productId: selectedProduct.id,
      productName: selectedProduct.deviceName,
      amount,
      currency: selectedProduct.currency,
      date: paymentDate.toISOString(),
      installmentNumber: newInstallmentNumber,
      invoiceNumber: 0, // سێرڤەر ژمارەی ڕاستەقینە دادەنێت
      createdAt: new Date().toISOString(),
    };

    // تۆمارکردن لە داتابەیس
    const savedPayment = await addPaymentToDatabase(newPayment);
    if (savedPayment) {
      setPaymentHistory((prev) => [savedPayment, ...prev]);

      const updatedProduct: Product = {
        ...selectedProduct,
        paidAmount: selectedProduct.paidAmount + amount,
        remainingAmount: selectedProduct.salePrice - (selectedProduct.paidAmount + amount),
        installments: newInstallmentNumber,
      };
      onUpdateProduct(updatedProduct);

      setPaymentAmount("");
      setPaymentDate(new Date());
      setSelectedProductId("");
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-background flex flex-col md:flex-row overflow-hidden touch-none" 
      dir="rtl"
      style={{ overscrollBehavior: 'contain' }}
      onTouchMove={(e) => {
        // ڕێگری لە سکرۆڵی دەرەوە لە مۆبایل
        const target = e.target as HTMLElement;
        const scrollableParent = target.closest('[data-scrollable="true"]');
        if (!scrollableParent) {
          e.preventDefault();
        }
      }}
    >
      {/* Mobile Header */}
      <header className="md:hidden border-b bg-background z-10">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="font-bold text-base">{customer.fullName}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`md:hidden fixed inset-0 top-14 z-40 transition-all duration-300 ease-out ${
          mobileMenuOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div 
          className={`absolute top-0 right-0 h-full w-72 bg-card border-l shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()}
          data-scrollable="true"
          style={{ overscrollBehavior: 'contain' }}
        >
          {/* Logo Header in Mobile Menu */}
          <div className="flex items-center gap-3 h-16 px-4 border-b">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center shadow-md">
              <span className="text-primary-foreground font-bold text-xl">ق</span>
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-sm">پێشانگای قەندیل</span>
              <span className="text-xs text-muted-foreground">سیستەمی بەڕێوەبردن</span>
            </div>
          </div>
          <nav className="p-4 space-y-2">
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => { onClose(); onNavigate?.("dashboard"); setMobileMenuOpen(false); }}
            >
              <LayoutDashboard className="h-5 w-5" />
              داشبۆرد
            </button>
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => { onClose(); onNavigate?.("customers"); setMobileMenuOpen(false); }}
            >
              <Users className="h-5 w-5" />
              کڕیارەکان
            </button>
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => { onClose(); onNavigate?.("sales"); setMobileMenuOpen(false); }}
            >
              <ShoppingCart className="h-5 w-5" />
              فرۆشتنەکان
            </button>
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 bg-primary/10 text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              <CreditCard className="h-5 w-5" />
              پارەدان
            </button>
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => { onClose(); onNavigate?.("expenses"); setMobileMenuOpen(false); }}
            >
              <Receipt className="h-5 w-5" />
              خەرجییەکان
            </button>
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => { onClose(); onNavigate?.("notifications"); setMobileMenuOpen(false); }}
            >
              <Bell className="h-5 w-5" />
              دواکەوتووەکان
            </button>
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => { onClose(); onNavigate?.("reports"); setMobileMenuOpen(false); }}
            >
              <FileBarChart className="h-5 w-5" />
              ڕاپۆرتەکان
            </button>
            <div className="border-t my-2" />
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-all duration-200"
              onClick={() => { onClose(); onNavigate?.("settings"); setMobileMenuOpen(false); }}
            >
              <Settings className="h-5 w-5" />
              ڕێکخستن
            </button>
          </nav>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-56 border-l bg-white dark:bg-muted/30 flex-col h-screen">
        <div className="flex items-center gap-3 h-16 px-4 border-b">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Smartphone className="h-5 w-5 text-primary" />
          </div>
          <span className="font-semibold text-sm">پێشانگای قەندیل</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <button onClick={() => { onClose(); onNavigate?.("dashboard"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <LayoutDashboard className="h-4 w-4" />
            داشبۆرد
          </button>
          <button onClick={() => { onClose(); onNavigate?.("customers"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <Users className="h-4 w-4" />
            کڕیارەکان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("sales"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <ShoppingCart className="h-4 w-4" />
            فرۆشتنەکان
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-primary/10 text-primary cursor-pointer">
            <CreditCard className="h-4 w-4" />
            پارەدان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("expenses"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <Receipt className="h-4 w-4" />
            خەرجییەکان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("notifications"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <Bell className="h-4 w-4" />
            دواکەوتووەکان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("reports"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <FileBarChart className="h-4 w-4" />
            ڕاپۆرتەکان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("settings"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <Settings className="h-4 w-4" />
            ڕێکخستن
          </button>
        </nav>
        <div className="p-3 border-t">
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <LogOut className="h-4 w-4" />
            چوونەدەرەوە
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Desktop Top Bar */}
        <div className="hidden md:block border-b bg-background z-10">
          <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-xs h-5">
                <Hash className="h-3 w-3 ml-1" />
                {customer.customerCode}
              </Badge>
              <h1 className="text-base font-semibold">{customer.fullName}</h1>
              {customer.job && (
                <Badge variant="secondary" className="text-xs h-5">
                  <Briefcase className="h-3 w-3 ml-1" />
                  {customer.job}
                </Badge>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="h-6 w-6 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 cursor-pointer">
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" align="start" dir="rtl">
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">مۆبایل ١:</span>
                      <span className="font-medium" dir="ltr">{customer.phone1}</span>
                    </div>
                    {customer.phone2 && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">مۆبایل ٢:</span>
                        <span className="font-medium" dir="ltr">{customer.phone2}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">ناونیشان:</span>
                        <span className="font-medium">{customer.address}</span>
                      </div>
                    )}
                    {customer.guarantorName && (
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">کەفیل:</span>
                        <span className="font-medium">{customer.guarantorName}</span>
                      </div>
                    )}
                    {customer.guarantorPhone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">ژمارەی کەفیل:</span>
                        <span className="font-medium" dir="ltr">{customer.guarantorPhone}</span>
                      </div>
                    )}
                    {customer.notes && (
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">تێبینی:</span>
                        <span className="font-medium">{customer.notes}</span>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              گەڕانەوە
              <ArrowLeft className="h-4 w-4 mr-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div 
        className="overflow-y-auto flex-1 p-3 md:p-6 space-y-3 md:space-y-4" 
        data-scrollable="true"
        style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
      >
        {/* Summary Cards - Minimal Design */}
        <div className="grid grid-cols-2 gap-3">
          {/* IQD Card */}
          <div className="rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Banknote className="h-4 w-4 text-amber-600" />
              </div>
              <span className="font-semibold">دینار</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{activeTab === "payment" ? "قیست" : "فرۆشتن"}</span>
                <span className="font-medium" dir="ltr">{(activeTab === "payment" ? totalMonthlyIQD : totalSaleIQD).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">وەرگیراو</span>
                <span className="font-medium text-green-600" dir="ltr">{totalPaidIQD.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ماوە</span>
                <span className="font-medium text-red-500" dir="ltr">{totalRemainingIQD.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* USD Card */}
          <div className="rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </div>
              <span className="font-semibold">دۆلار</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{activeTab === "payment" ? "قیست" : "فرۆشتن"}</span>
                <span className="font-medium" dir="ltr">{(activeTab === "payment" ? totalMonthlyUSD : totalSaleUSD).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">وەرگیراو</span>
                <span className="font-medium text-green-600" dir="ltr">{totalPaidUSD.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ماوە</span>
                <span className="font-medium text-red-500" dir="ltr">{totalRemainingUSD.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" dir="rtl">
          <TabsList className="w-full h-auto p-1 bg-muted/50 rounded-lg border gap-1 grid grid-cols-2 md:grid-cols-4">
            <TabsTrigger 
              value="products" 
              className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <Package className="h-4 w-4" />
              <span>کاڵاکان</span>
            </TabsTrigger>
            <TabsTrigger 
              value="payment" 
              className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <CreditCard className="h-4 w-4" />
              <span>پارەدان</span>
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <History className="h-4 w-4" />
              <span>مێژوو</span>
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <CheckCircle className="h-4 w-4" />
              <span>تەواوبوو</span>
            </TabsTrigger>
          </TabsList>

          <div className="mt-3 rounded-lg border bg-card p-3 md:p-4" dir="rtl">
            {/* Tab 1: Products - Table Format */}
            <TabsContent value="products" className="mt-0 space-y-2 md:space-y-4">
              {!hideAddProduct && (
                <div className="flex items-center justify-between mb-3">
                  <AddProductDialog
                    customerId={customer.id}
                    onAdd={onAddProduct}
                    trigger={
                      <Button size="sm" className="h-8 md:h-9 text-xs md:text-sm">
                        <Plus className="h-3 w-3 md:h-4 md:w-4 ml-1 md:mr-2" />
                        <span>زیادکردنی کاڵا</span>
                      </Button>
                    }
                  />
                </div>
              )}
              
              {/* Products */}
              {activeProducts.length === 0 ? (
                <div className="text-center py-8 md:py-12 text-muted-foreground border rounded-lg text-sm">
                  هیچ کاڵایەک نییە
                </div>
              ) : (
                <>
                  {/* Header with count */}
                  <div className="flex items-center justify-between mb-3 pb-2 border-b">
                    <span className="text-sm text-muted-foreground">کۆی کاڵاکان: <span className="font-medium text-foreground">{activeProducts.length}</span></span>
                  </div>
                  
                  {/* Desktop Table */}
                  <div className="rounded-md border hidden md:block" dir="rtl">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right w-12">#</TableHead>
                          <TableHead className="text-right">ناوی ئامێر</TableHead>
                          <TableHead className="text-right">نرخی فرۆشتن</TableHead>
                          <TableHead className="text-right">وەرگیراو</TableHead>
                          <TableHead className="text-right">ماوە</TableHead>
                          <TableHead className="text-right">قیستی مانگانە</TableHead>
                          <TableHead className="text-right w-24">کردارەکان</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeProducts.map((product, index) => (
                          <TableRow key={product.id} className={index % 2 === 1 ? 'bg-muted/50' : ''}>
                            <TableCell className="text-center font-medium text-muted-foreground">
                              {index + 1}
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{product.deviceName}</span>
                            </TableCell>
                            <TableCell dir="ltr" className="text-right">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">{product.salePrice.toLocaleString()}</span>
                                <span className="text-xs text-muted-foreground">
                                  {product.currency === "IQD" ? "د.ع" : "$"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell dir="ltr" className="text-right text-green-600">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">{product.paidAmount.toLocaleString()}</span>
                                <span className="text-xs">{product.currency === "IQD" ? "د.ع" : "$"}</span>
                              </div>
                            </TableCell>
                            <TableCell dir="ltr" className="text-right text-destructive">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">{product.remainingAmount.toLocaleString()}</span>
                                <span className="text-xs">{product.currency === "IQD" ? "د.ع" : "$"}</span>
                              </div>
                            </TableCell>
                            <TableCell dir="ltr" className="text-right text-blue-600">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">{product.monthlyInstallment.toLocaleString()}</span>
                                <span className="text-xs">{product.currency === "IQD" ? "د.ع" : "$"}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                                  <MoreHorizontal className="h-4 w-4" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="text-right w-48">
                                  <DropdownMenuItem onSelect={() => {
                                    // دەستکاری - دەبێت دایەلۆگ بکرێتەوە
                                  }}>
                                    <Pencil className="h-4 w-4 ml-2" />
                                    دەستکاری
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onSelect={() => {
                                      if (confirm("دڵنیایت لە سڕینەوەی ئەم کاڵایە؟")) {
                                        onDeleteProduct(product.id);
                                      }
                                    }}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 ml-2" />
                                    سڕینەوە
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-3">
                    {activeProducts.map((product, index) => {
                      const isExpanded = expandedProducts.has(product.id);
                      const isEven = index % 2 === 1;
                      return (
                        <div key={product.id} className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}>
                          <div className="p-3 cursor-pointer" onClick={() => toggleExpanded(product.id)}>
                            {/* Main Row */}
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-bold text-primary">{index + 1}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm truncate">{product.deviceName}</span>
                                  <Badge variant={product.currency === "IQD" ? "outline" : "secondary"} className="text-[10px] px-1.5 h-4">
                                    {product.currency === "IQD" ? "د.ع" : "$"}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {product.deviceDescription || product.date}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpanded(product.id);
                                  }}
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger onClick={(e) => e.stopPropagation()} className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="text-right w-48">
                                    <DropdownMenuItem onSelect={() => {}}>
                                      <Pencil className="h-4 w-4 ml-2" />
                                      دەستکاری
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onSelect={() => {
                                        if (confirm("دڵنیایت لە سڕینەوەی ئەم کاڵایە؟")) {
                                          onDeleteProduct(product.id);
                                        }
                                      }}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 ml-2" />
                                      سڕینەوە
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                            {/* Mini Table */}
                            <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2" dir="rtl">
                              <div className="text-center">
                                <div className="text-muted-foreground text-[10px]">نرخ</div>
                                <div className="font-semibold" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.salePrice.toLocaleString()}</>}</div>
                              </div>
                              <div className="text-center border-x border-dashed">
                                <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                <div className="font-semibold text-green-600" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-muted-foreground text-[10px]">ماوە</div>
                                <div className="font-semibold text-red-500" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.remainingAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.remainingAmount.toLocaleString()}</>}</div>
                              </div>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                              <div className="space-y-2 pt-3">
                                <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                  <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                                  <span className="font-medium text-sm" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.monthlyInstallment.toLocaleString()}</>}</span>
                                </div>
                                <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                  <span className="text-muted-foreground text-xs">ژمارەی قیستەکان</span>
                                  <span className="font-medium text-sm">{product.installments || 0}</span>
                                </div>
                                <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                  <span className="text-muted-foreground text-xs">بەروار</span>
                                  <span className="font-medium text-sm" dir="ltr">{product.date}</span>
                                </div>
                                {product.deviceDescription && (
                                  <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                    <span className="text-muted-foreground text-xs">وەسف</span>
                                    <span className="font-medium text-sm">{product.deviceDescription}</span>
                                  </div>
                                )}
                                {product.notes && (
                                  <div className="pt-2 mt-1 border-t">
                                    <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                                    <p className="text-sm bg-background rounded-lg p-2 text-muted-foreground">{product.notes}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Tab 2: Payment */}
            <TabsContent value="payment" className="mt-0 space-y-4">
              {/* Currency Toggle and Amount Input */}
              {(() => {
                const currencyActiveProducts = activeProducts.filter(p => p.currency === paymentCurrency && p.remainingAmount > 0);
                const totalMonthlyForCurrency = currencyActiveProducts.reduce((sum, p) => sum + p.monthlyInstallment, 0);
                
                // Initialize with default value once when entering payment tab
                if (!isGlobalPaymentInitialized && activeTab === "payment" && currencyActiveProducts.length > 0) {
                  const initAmount = totalMonthlyForCurrency > 0 ? totalMonthlyForCurrency : currencyActiveProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
                  setGlobalPaymentAmount(initAmount.toString());
                  setIsGlobalPaymentInitialized(true);
                  
                  // Also distribute immediately
                  const newProductPayments: Record<string, string> = {};
                  const roundToValid = (value: number) => {
                    if (paymentCurrency === "USD") return Math.round(value);
                    return Math.round(value / 250) * 250;
                  };
                  
                  const totalRemainingForCurrency = currencyActiveProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
                  const effectiveAmount = Math.min(initAmount, totalRemainingForCurrency);
                  
                  const productAmounts: { id: string; amount: number; remaining: number; monthly: number }[] = [];
                  
                  // ئەگەر بڕی پارەدان کەمتر یان یەکسانە لەگەڵ کۆی قیستی مانگانە
                  if (initAmount <= totalMonthlyForCurrency && totalMonthlyForCurrency > 0) {
                    for (const product of currencyActiveProducts) {
                      const ratio = product.monthlyInstallment / totalMonthlyForCurrency;
                      const rawAmount = effectiveAmount * ratio;
                      const roundedAmount = roundToValid(rawAmount);
                      const payAmount = Math.min(roundedAmount, product.remainingAmount);
                      productAmounts.push({ id: product.id, amount: payAmount, remaining: product.remainingAmount, monthly: product.monthlyInstallment });
                    }
                  } else {
                    // سەرەتا هەموو قیستەکان پڕ بکەرەوە، دواتر زیادەکە دابەش بکە
                    let remainingToDistribute = effectiveAmount;
                    
                    for (const product of currencyActiveProducts) {
                      const monthlyAmount = Math.min(product.monthlyInstallment, product.remainingAmount);
                      productAmounts.push({ id: product.id, amount: monthlyAmount, remaining: product.remainingAmount, monthly: product.monthlyInstallment });
                      remainingToDistribute -= monthlyAmount;
                    }
                    
                    if (remainingToDistribute > 0) {
                      const totalRemainingAfterMonthly = productAmounts.reduce((sum, p) => sum + (p.remaining - p.amount), 0);
                      if (totalRemainingAfterMonthly > 0) {
                        for (const p of productAmounts) {
                          const remainingForProduct = p.remaining - p.amount;
                          if (remainingForProduct > 0) {
                            const ratio = remainingForProduct / totalRemainingAfterMonthly;
                            const extraAmount = Math.min(
                              roundToValid(remainingToDistribute * ratio),
                              remainingForProduct
                            );
                            p.amount += extraAmount;
                          }
                        }
                      }
                    }
                  }
                  
                  // چاککردنی جیاوازی گردکردنەوە
                  let distributedTotal = productAmounts.reduce((sum, p) => sum + p.amount, 0);
                  const targetAmount = roundToValid(effectiveAmount);
                  let difference = targetAmount - distributedTotal;
                  if (difference !== 0 && productAmounts.length > 0) {
                    const sortedProducts = [...productAmounts].sort((a, b) => (b.remaining - b.amount) - (a.remaining - a.amount));
                    for (const p of sortedProducts) {
                      const newAmount = p.amount + difference;
                      if (newAmount > 0 && newAmount <= p.remaining) {
                        p.amount = newAmount;
                        break;
                      }
                    }
                  }
                  
                  for (const p of productAmounts) {
                    if (p.amount > 0) {
                      newProductPayments[p.id] = p.amount.toString();
                    }
                  }
                  
                  setProductPayments(newProductPayments);
                }
                
                // Real-time distribution function
                const distributePayment = (amount: string) => {
                  const totalAmount = parseFloat(amount) || 0;
                  
                  if (totalAmount <= 0 || currencyActiveProducts.length === 0) {
                    setProductPayments({});
                    return;
                  }
                  
                  const newProductPayments: Record<string, string> = {};
                  
                  // Round to nearest valid currency denomination
                  // IQD: 250, USD: 1
                  const roundToValidDenomination = (value: number, currency: "IQD" | "USD") => {
                    if (currency === "USD") {
                      return Math.round(value);
                    }
                    return Math.round(value / 250) * 250;
                  };
                  
                  // کۆی ماوەی هەموو کاڵاکان
                  const totalRemainingForCurrency = currencyActiveProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
                  
                  // ئەگەر بڕی پارەدان زیاترە لە کۆی ماوە، تەنها کۆی ماوە دابەش بکە
                  const effectiveAmount = Math.min(totalAmount, totalRemainingForCurrency);
                  
                  if (effectiveAmount <= 0) {
                    setProductPayments({});
                    return;
                  }
                  
                  const productAmounts: { id: string; amount: number; remaining: number; monthly: number }[] = [];
                  
                  // ئەگەر بڕی پارەدان کەمتر یان یەکسانە لەگەڵ کۆی قیستی مانگانە
                  if (totalAmount <= totalMonthlyForCurrency && totalMonthlyForCurrency > 0) {
                    // دابەشکردن بە ڕێژەی قیستی مانگانە
                    let distributedTotal = 0;
                    for (const product of currencyActiveProducts) {
                      const ratio = product.monthlyInstallment / totalMonthlyForCurrency;
                      const rawAmount = effectiveAmount * ratio;
                      const roundedAmount = roundToValidDenomination(rawAmount, paymentCurrency);
                      const payAmount = Math.min(roundedAmount, product.remainingAmount);
                      productAmounts.push({ id: product.id, amount: payAmount, remaining: product.remainingAmount, monthly: product.monthlyInstallment });
                      distributedTotal += payAmount;
                    }
                    
                    // چاککردنی جیاوازی گردکردنەوە
                    const targetAmount = roundToValidDenomination(effectiveAmount, paymentCurrency);
                    let difference = targetAmount - distributedTotal;
                    if (difference !== 0 && productAmounts.length > 0) {
                      const sortedProducts = [...productAmounts].sort((a, b) => (b.remaining - b.amount) - (a.remaining - a.amount));
                      for (const p of sortedProducts) {
                        const newAmount = p.amount + difference;
                        if (newAmount > 0 && newAmount <= p.remaining) {
                          p.amount = newAmount;
                          break;
                        }
                      }
                    }
                  } else {
                    // بڕی پارەدان زیاترە لە کۆی قیستی مانگانە
                    // سەرەتا هەموو قیستەکان پڕ بکەرەوە، دواتر زیادەکە دابەش بکە
                    let remainingToDistribute = effectiveAmount;
                    
                    // قۆناغی ١: هەموو قیستی مانگانەکان پڕ بکەرەوە
                    for (const product of currencyActiveProducts) {
                      const monthlyAmount = Math.min(product.monthlyInstallment, product.remainingAmount);
                      productAmounts.push({ id: product.id, amount: monthlyAmount, remaining: product.remainingAmount, monthly: product.monthlyInstallment });
                      remainingToDistribute -= monthlyAmount;
                    }
                    
                    // قۆناغی ٢: زیادەکە دابەش بکە بە ڕێژەی ماوە
                    if (remainingToDistribute > 0) {
                      const totalRemainingAfterMonthly = productAmounts.reduce((sum, p) => sum + (p.remaining - p.amount), 0);
                      
                      if (totalRemainingAfterMonthly > 0) {
                        for (const p of productAmounts) {
                          const remainingForProduct = p.remaining - p.amount;
                          if (remainingForProduct > 0) {
                            const ratio = remainingForProduct / totalRemainingAfterMonthly;
                            const extraAmount = Math.min(
                              roundToValidDenomination(remainingToDistribute * ratio, paymentCurrency),
                              remainingForProduct
                            );
                            p.amount += extraAmount;
                          }
                        }
                      }
                    }
                    
                    // چاککردنی جیاوازی گردکردنەوە
                    let distributedTotal = productAmounts.reduce((sum, p) => sum + p.amount, 0);
                    const targetAmount = roundToValidDenomination(effectiveAmount, paymentCurrency);
                    let difference = targetAmount - distributedTotal;
                    if (difference !== 0 && productAmounts.length > 0) {
                      const sortedProducts = [...productAmounts].sort((a, b) => (b.remaining - b.amount) - (a.remaining - a.amount));
                      for (const p of sortedProducts) {
                        const newAmount = p.amount + difference;
                        if (newAmount > 0 && newAmount <= p.remaining) {
                          p.amount = newAmount;
                          break;
                        }
                      }
                    }
                  }
                  
                  for (const p of productAmounts) {
                    if (p.amount > 0) {
                      newProductPayments[p.id] = p.amount.toString();
                    }
                  }
                  
                  setProductPayments(newProductPayments);
                };
                
                return (
                  <>
                    {/* Currency Toggle - Segmented Control Style */}
                    <div className="flex items-center p-1.5 bg-muted/50 rounded-xl border mb-3">
                      <button
                        onClick={() => { setPaymentCurrency("IQD"); setSelectedProductId(""); setPaymentAmount(""); setGlobalPaymentAmount(""); setProductPayments({}); setIsGlobalPaymentInitialized(false); }}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          paymentCurrency === "IQD" 
                            ? "bg-amber-500 text-white shadow-md" 
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                      >
                        <Banknote className="h-4 w-4" />
                        <span>پارەدان بە دینار</span>
                      </button>
                      <button
                        onClick={() => { setPaymentCurrency("USD"); setSelectedProductId(""); setPaymentAmount(""); setGlobalPaymentAmount(""); setProductPayments({}); setIsGlobalPaymentInitialized(false); }}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          paymentCurrency === "USD" 
                            ? "bg-emerald-500 text-white shadow-md" 
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                      >
                        <DollarSign className="h-4 w-4" />
                        <span>پارەدان بە دۆلار</span>
                      </button>
                    </div>
                    
                    {/* Amount Input, Date Picker and Action Buttons */}
                    <div className="flex flex-col gap-3">
                      {/* Amount and Date Row */}
                      <div className="flex flex-col md:flex-row gap-3">
                        {(() => {
                          const totalRemainingForCurrency = currencyActiveProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
                          
                          return (
                            <>
                              {/* Amount Input with Clear Button */}
                              <div className="flex-1">
                                <Label className="text-xs text-muted-foreground mb-1.5 block">بڕی پارەدان</Label>
                                <div className="flex gap-2">
                                  <Input
                                    type="text"
                                    placeholder={`بڕ بە ${paymentCurrency === "IQD" ? "د.ع" : "$"}`}
                                    dir="ltr"
                                    className="h-10 text-sm flex-1"
                                    value={formatNumberWithCommas(globalPaymentAmount)}
                                    onChange={(e) => {
                                      const rawValue = parseFormattedNumber(e.target.value);
                                      if (rawValue === '' || /^\d+$/.test(rawValue)) {
                                        const numValue = parseFloat(rawValue) || 0;
                                        // ڕێگری لە نووسینی زیاتر لە ماوە
                                        if (numValue > totalRemainingForCurrency) {
                                          setGlobalPaymentAmount(totalRemainingForCurrency.toString());
                                          distributePayment(totalRemainingForCurrency.toString());
                                        } else {
                                          setGlobalPaymentAmount(rawValue);
                                          distributePayment(rawValue);
                                        }
                                      }
                                    }}
                                    onPaste={(e) => {
                                      e.preventDefault();
                                      const pastedText = e.clipboardData.getData('text');
                                      const rawValue = pastedText.replace(/[^\d]/g, '');
                                      if (rawValue) {
                                        const numValue = parseFloat(rawValue) || 0;
                                        // ڕێگری لە پەیستکردنی زیاتر لە ماوە
                                        if (numValue > totalRemainingForCurrency) {
                                          setGlobalPaymentAmount(totalRemainingForCurrency.toString());
                                          distributePayment(totalRemainingForCurrency.toString());
                                        } else {
                                          setGlobalPaymentAmount(rawValue);
                                          distributePayment(rawValue);
                                        }
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="default"
                                    size="icon"
                                    onClick={() => { setGlobalPaymentAmount(""); setPaymentAmount(""); setSelectedProductId(""); setProductPayments({}); setGlobalPaymentDate(new Date()); }}
                                    className="h-10 w-10 flex-shrink-0 md:hidden"
                                  >
                                    <Eraser className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="default"
                                    onClick={() => { setGlobalPaymentAmount(""); setPaymentAmount(""); setSelectedProductId(""); setProductPayments({}); setGlobalPaymentDate(new Date()); }}
                                    className="h-10 hidden md:flex items-center gap-2 px-4"
                                  >
                                    <Eraser className="h-4 w-4" />
                                    <span>پاککردنەوەی گشتی</span>
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Date Picker */}
                              <div className="md:w-64">
                                <DialogDatePicker
                                  value={globalPaymentDate}
                                  onChange={setGlobalPaymentDate}
                                  label="بەرواری پارەدان"
                                />
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Products Table for Selected Currency */}
                    {currencyActiveProducts.length === 0 ? (
                      <div className="text-center py-6 md:py-8 text-muted-foreground border rounded-lg text-xs md:text-sm">
                        هیچ کاڵایەک بە {paymentCurrency === "IQD" ? "دینار" : "دۆلار"} نییە
                      </div>
                    ) : (
                      <>
                        {/* Payment Products Table - Desktop */}
                        <div className="hidden md:block rounded-md border" dir="rtl">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-right w-12">#</TableHead>
                                <TableHead className="text-right">ناوی ئامێر</TableHead>
                                <TableHead className="text-right">قیستی مانگانە</TableHead>
                                <TableHead className="text-right">وەرگیراو</TableHead>
                                <TableHead className="text-right">ماوە</TableHead>
                                <TableHead className="text-right">جاری داهاتوو</TableHead>
                                <TableHead className="text-right w-32">پارەدان</TableHead>
                                <TableHead className="text-right w-24">کردارەکان</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {currencyActiveProducts.map((product, index) => (
                                <TableRow key={product.id} className={index % 2 === 1 ? 'bg-muted/50' : ''}>
                                  <TableCell className="text-center font-medium text-muted-foreground">
                                    {index + 1}
                                  </TableCell>
                                  <TableCell>
                                    <span className="font-medium text-sm">{product.deviceName}</span>
                                  </TableCell>
                                  <TableCell dir="ltr" className="text-right text-blue-600 font-medium">
                                    <div className="flex items-center gap-1">
                                      <span>{product.monthlyInstallment.toLocaleString()}</span>
                                      <span className="text-xs">{paymentCurrency === "IQD" ? "د.ع" : "$"}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell dir="ltr" className="text-right text-green-600">
                                    <div className="flex items-center gap-1">
                                      <span>{product.paidAmount.toLocaleString()}</span>
                                      <span className="text-xs">{paymentCurrency === "IQD" ? "د.ع" : "$"}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell dir="ltr" className="text-right text-destructive font-medium">
                                    <div className="flex items-center gap-1">
                                      <span>{product.remainingAmount.toLocaleString()}</span>
                                      <span className="text-xs">{paymentCurrency === "IQD" ? "د.ع" : "$"}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {(() => {
                                      const productPaymentHistory = paymentHistory.filter(p => p.productId === product.id);
                                      const maxHistoryInstallment = productPaymentHistory.length > 0 
                                        ? Math.max(...productPaymentHistory.map(p => p.installmentNumber))
                                        : 0;
                                      const minInstallment = maxHistoryInstallment + 1;
                                      const currentInstallments = productInstallments[product.id] ?? minInstallment;
                                      
                                      return (
                                        <Input
                                          type="number"
                                          min={minInstallment}
                                          value={currentInstallments}
                                          onChange={(e) => {
                                            const newValue = parseInt(e.target.value) || 0;
                                            if (newValue >= minInstallment) {
                                              setProductInstallments(prev => ({
                                                ...prev,
                                                [product.id]: newValue
                                              }));
                                            }
                                          }}
                                          className="h-8 w-16 text-sm text-center"
                                          dir="ltr"
                                        />
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      type="text"
                                      placeholder="بڕ"
                                      dir="ltr"
                                      className="h-8 text-sm w-auto min-w-[80px] max-w-[150px]"
                                      value={formatNumberWithCommas(productPayments[product.id] || "")}
                                      onChange={(e) => {
                                        const rawValue = parseFormattedNumber(e.target.value);
                                        if (rawValue === '' || /^\d+$/.test(rawValue)) {
                                          const numValue = parseFloat(rawValue) || 0;
                                          // ڕێگری لە نووسینی زیاتر لە ماوەی کاڵا
                                          if (numValue > product.remainingAmount) {
                                            setProductPayments(prev => ({
                                              ...prev,
                                              [product.id]: product.remainingAmount.toString()
                                            }));
                                          } else {
                                            setProductPayments(prev => ({
                                              ...prev,
                                              [product.id]: rawValue
                                            }));
                                          }
                                        }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="h-8 px-2 text-xs"
                                      onClick={() => {
                                        const removedAmount = parseFloat(productPayments[product.id] || "0");
                                        if (removedAmount <= 0) {
                                          setProductPayments(prev => {
                                            const newPayments = { ...prev };
                                            delete newPayments[product.id];
                                            return newPayments;
                                          });
                                          return;
                                        }
                                        
                                        const otherProductsWithPayments = currencyActiveProducts.filter(p => 
                                          p.id !== product.id && productPayments[p.id] && parseFloat(productPayments[p.id]) > 0
                                        );
                                        const otherTotalMonthly = otherProductsWithPayments.reduce((sum, p) => sum + p.monthlyInstallment, 0);
                                        
                                        if (otherTotalMonthly === 0 || otherProductsWithPayments.length === 0) {
                                          setProductPayments(prev => {
                                            const newPayments = { ...prev };
                                            delete newPayments[product.id];
                                            return newPayments;
                                          });
                                          return;
                                        }
                                        
                                        const roundToValidDenomination = (value: number) => {
                                          if (paymentCurrency === "USD") {
                                            return Math.round(value);
                                          }
                                          return Math.round(value / 250) * 250;
                                        };
                                        
                                        setProductPayments(prev => {
                                          const newPayments = { ...prev };
                                          delete newPayments[product.id];
                                          
                                          let distributedTotal = 0;
                                          const productAmounts: { id: string; amount: number; remaining: number }[] = [];
                                          
                                          for (const otherProduct of otherProductsWithPayments) {
                                            const ratio = otherProduct.monthlyInstallment / otherTotalMonthly;
                                            const additionalAmount = roundToValidDenomination(removedAmount * ratio);
                                            const currentAmount = parseFloat(newPayments[otherProduct.id] || "0");
                                            const newAmount = Math.min(currentAmount + additionalAmount, otherProduct.remainingAmount);
                                            productAmounts.push({ id: otherProduct.id, amount: newAmount, remaining: otherProduct.remainingAmount });
                                            distributedTotal += additionalAmount;
                                          }
                                          
                                          const difference = roundToValidDenomination(removedAmount) - distributedTotal;
                                          if (difference !== 0 && productAmounts.length > 0) {
                                            const sortedProducts = [...productAmounts].sort((a, b) => b.amount - a.amount);
                                            for (const p of sortedProducts) {
                                              const adjustedAmount = p.amount + difference;
                                              if (adjustedAmount > 0 && adjustedAmount <= p.remaining) {
                                                p.amount = adjustedAmount;
                                                break;
                                              }
                                            }
                                          }
                                          
                                          for (const p of productAmounts) {
                                            newPayments[p.id] = p.amount.toString();
                                          }
                                          
                                          return newPayments;
                                        });
                                      }}
                                    >
                                      <Eraser className="h-3 w-3 mr-1" />
                                      <span>پاککردنەوە</span>
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Payment Products Cards - Mobile */}
                        <div className="md:hidden space-y-3">
                          {currencyActiveProducts.map((product, index) => {
                            const productPaymentHistory = paymentHistory.filter(p => p.productId === product.id);
                            const maxHistoryInstallment = productPaymentHistory.length > 0 
                              ? Math.max(...productPaymentHistory.map(p => p.installmentNumber))
                              : 0;
                            const minInstallment = maxHistoryInstallment + 1;
                            const currentInstallments = productInstallments[product.id] ?? minInstallment;
                            
                            return (
                              <div key={product.id} className="rounded-lg border bg-card p-3 space-y-2.5" dir="rtl">
                                <div className="flex items-start justify-between gap-2 pb-2 border-b">
                                  <div className="flex-1">
                                    <div className="font-semibold text-sm">{product.deviceName}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">کاڵای ژمارە {index + 1}</div>
                                  </div>
                                </div>
                                
                                {/* Mini Table */}
                                <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2" dir="rtl">
                                  <div className="text-center">
                                    <div className="text-muted-foreground text-[10px]">قیست</div>
                                    <div className="font-semibold text-blue-600" dir={paymentCurrency === "IQD" ? "rtl" : "ltr"}>
                                      {paymentCurrency === "IQD" ? (
                                        <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px]">د.ع</span></>
                                      ) : (
                                        <><span className="text-[10px]">$ </span>{product.monthlyInstallment.toLocaleString()}</>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-center border-x border-dashed">
                                    <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                    <div className="font-semibold text-green-600" dir={paymentCurrency === "IQD" ? "rtl" : "ltr"}>
                                      {paymentCurrency === "IQD" ? (
                                        <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></>
                                      ) : (
                                        <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-muted-foreground text-[10px]">ماوە</div>
                                    <div className="font-semibold text-red-500" dir={paymentCurrency === "IQD" ? "rtl" : "ltr"}>
                                      {paymentCurrency === "IQD" ? (
                                        <>{product.remainingAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></>
                                      ) : (
                                        <><span className="text-[10px]">$ </span>{product.remainingAmount.toLocaleString()}</>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="space-y-2 pt-2 border-t">
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1.5">
                                      <Label className="text-xs text-muted-foreground">جاری داهاتوو</Label>
                                      <Input
                                        type="number"
                                        min={minInstallment}
                                        value={currentInstallments}
                                        onChange={(e) => {
                                          const newValue = parseInt(e.target.value) || 0;
                                          if (newValue >= minInstallment) {
                                            setProductInstallments(prev => ({
                                              ...prev,
                                              [product.id]: newValue
                                            }));
                                          }
                                        }}
                                        className="h-9 text-sm text-center"
                                        dir="ltr"
                                      />
                                    </div>
                                    <div className="space-y-1.5">
                                      <Label className="text-xs text-muted-foreground">بڕی پارەدان</Label>
                                      <div className="flex gap-1.5">
                                        <Input
                                          type="text"
                                          placeholder="بڕ"
                                          dir="ltr"
                                          className="h-9 text-sm flex-1"
                                          value={formatNumberWithCommas(productPayments[product.id] || "")}
                                          onChange={(e) => {
                                            const rawValue = parseFormattedNumber(e.target.value);
                                            if (rawValue === '' || /^\d+$/.test(rawValue)) {
                                              const numValue = parseFloat(rawValue) || 0;
                                              // ڕێگری لە نووسینی زیاتر لە ماوەی کاڵا
                                              if (numValue > product.remainingAmount) {
                                                setProductPayments(prev => ({
                                                  ...prev,
                                                  [product.id]: product.remainingAmount.toString()
                                                }));
                                              } else {
                                                setProductPayments(prev => ({
                                                  ...prev,
                                                  [product.id]: rawValue
                                                }));
                                              }
                                            }
                                          }}
                                        />
                                        <Button
                                          size="icon"
                                          variant="default"
                                          className="h-9 w-9 flex-shrink-0"
                                          onClick={() => {
                                            const removedAmount = parseFloat(productPayments[product.id] || "0");
                                            if (removedAmount <= 0) {
                                              setProductPayments(prev => {
                                                const newPayments = { ...prev };
                                                delete newPayments[product.id];
                                                return newPayments;
                                              });
                                              return;
                                            }
                                            
                                            const otherProductsWithPayments = currencyActiveProducts.filter(p => 
                                              p.id !== product.id && productPayments[p.id] && parseFloat(productPayments[p.id]) > 0
                                            );
                                            const otherTotalMonthly = otherProductsWithPayments.reduce((sum, p) => sum + p.monthlyInstallment, 0);
                                            
                                            if (otherTotalMonthly === 0 || otherProductsWithPayments.length === 0) {
                                              setProductPayments(prev => {
                                                const newPayments = { ...prev };
                                                delete newPayments[product.id];
                                                return newPayments;
                                              });
                                              return;
                                            }
                                            
                                            const roundToValidDenomination = (value: number) => {
                                              if (paymentCurrency === "USD") {
                                                return Math.round(value);
                                              }
                                              return Math.round(value / 250) * 250;
                                            };
                                            
                                            setProductPayments(prev => {
                                              const newPayments = { ...prev };
                                              delete newPayments[product.id];
                                              
                                              let distributedTotal = 0;
                                              const productAmounts: { id: string; amount: number; remaining: number }[] = [];
                                              
                                              for (const otherProduct of otherProductsWithPayments) {
                                                const ratio = otherProduct.monthlyInstallment / otherTotalMonthly;
                                                const additionalAmount = roundToValidDenomination(removedAmount * ratio);
                                                const currentAmount = parseFloat(newPayments[otherProduct.id] || "0");
                                                const newAmount = Math.min(currentAmount + additionalAmount, otherProduct.remainingAmount);
                                                productAmounts.push({ id: otherProduct.id, amount: newAmount, remaining: otherProduct.remainingAmount });
                                                distributedTotal += additionalAmount;
                                              }
                                              
                                              const difference = roundToValidDenomination(removedAmount) - distributedTotal;
                                              if (difference !== 0 && productAmounts.length > 0) {
                                                const sortedProducts = [...productAmounts].sort((a, b) => b.amount - a.amount);
                                                for (const p of sortedProducts) {
                                                  const adjustedAmount = p.amount + difference;
                                                  if (adjustedAmount > 0 && adjustedAmount <= p.remaining) {
                                                    p.amount = adjustedAmount;
                                                    break;
                                                  }
                                                }
                                              }
                                              
                                              for (const p of productAmounts) {
                                                newPayments[p.id] = p.amount.toString();
                                              }
                                              
                                              return newPayments;
                                            });
                                          }}
                                        >
                                          <Eraser className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Payment Buttons - Responsive */}
                        <div className="flex flex-wrap items-center gap-2 pt-2">
                          {(() => {
                            const totalRemainingForCurrency = currencyActiveProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
                            const currentAmount = parseFloat(globalPaymentAmount) || 0;
                            const isOverpaying = currentAmount > totalRemainingForCurrency;
                            const hasValidPayments = Object.keys(productPayments).length > 0 && Object.values(productPayments).some(v => parseFloat(v) > 0);
                            
                            return (
                              <>
                          <Button
                            disabled={!hasValidPayments || isOverpaying}
                            className="h-8 md:h-9 px-3 md:px-4 text-xs md:text-sm"
                            onClick={async () => {
                              const productsToUpdate: Product[] = [];
                              const paymentsToAdd: PaymentHistory[] = [];
                              
                              for (const product of currencyActiveProducts) {
                                const paymentStr = productPayments[product.id];
                                if (!paymentStr) continue;
                                const amount = parseFloat(paymentStr);
                                if (amount <= 0) continue;
                                
                                const payAmount = Math.min(amount, product.remainingAmount);
                                if (payAmount > 0) {
                                  // بەکارهێنانی ژمارەی جاری گۆڕدراو یان جاری داهاتوو
                                  const productPaymentHist = paymentHistory.filter(p => p.productId === product.id);
                                  const maxHistInst = productPaymentHist.length > 0 
                                    ? Math.max(...productPaymentHist.map(p => p.installmentNumber))
                                    : 0;
                                  const nextInst = maxHistInst + 1;
                                  const newInstallmentNumber = productInstallments[product.id] ?? nextInst;
                                  const remainingAfterPayment = Math.max(0, product.remainingAmount - payAmount);
                                  
                                  paymentsToAdd.push({
                                    id: generateId(),
                                    customerId: customer.id,
                                    productId: product.id,
                                    productName: product.deviceName,
                                    amount: payAmount,
                                    currency: product.currency,
                                    date: globalPaymentDate.toISOString(),
                                    installmentNumber: newInstallmentNumber,
                                    invoiceNumber: 0, // سێرڤەر ژمارەی ڕاستەقینە دادەنێت
                                    remainingAfterPayment,
                                    createdAt: new Date().toISOString(),
                                  });
                                  productsToUpdate.push({
                                    ...product,
                                    paidAmount: product.paidAmount + payAmount,
                                    remainingAmount: remainingAfterPayment,
                                    installments: newInstallmentNumber,
                                  });
                                }
                              }
                              
                              if (productsToUpdate.length > 0) {
                                // پیشاندانی confirmation dialog
                                setPendingPaymentAction({
                                  productsToUpdate,
                                  paymentsToAdd,
                                  withPrint: false,
                                });
                                setShowPaymentConfirmation(true);
                              }
                            }}
                          >
                            <CreditCard className="h-3 w-3 md:h-4 md:w-4 ml-1 md:mr-2" />
                            پارەدان
                          </Button>
                          <Button
                            variant="secondary"
                            disabled={!hasValidPayments || isOverpaying}
                            className="h-8 md:h-9 px-3 md:px-4 text-xs md:text-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={async () => {
                              const productsToUpdate: Product[] = [];
                              const paymentsToAdd: PaymentHistory[] = [];
                              
                              for (const product of currencyActiveProducts) {
                                const paymentStr = productPayments[product.id];
                                if (!paymentStr) continue;
                                const amount = parseFloat(paymentStr);
                                if (amount <= 0) continue;
                                
                                const payAmount = Math.min(amount, product.remainingAmount);
                                if (payAmount > 0) {
                                  // بەکارهێنانی ژمارەی جاری گۆڕدراو یان جاری داهاتوو
                                  const productPaymentHist2 = paymentHistory.filter(p => p.productId === product.id);
                                  const maxHistInst2 = productPaymentHist2.length > 0 
                                    ? Math.max(...productPaymentHist2.map(p => p.installmentNumber))
                                    : 0;
                                  const nextInst2 = maxHistInst2 + 1;
                                  const newInstallmentNumber = productInstallments[product.id] ?? nextInst2;
                                  const remainingAfterPayment = Math.max(0, product.remainingAmount - payAmount);
                                  
                                  paymentsToAdd.push({
                                    id: generateId(),
                                    customerId: customer.id,
                                    productId: product.id,
                                    productName: product.deviceName,
                                    amount: payAmount,
                                    currency: product.currency,
                                    date: globalPaymentDate.toISOString(),
                                    installmentNumber: newInstallmentNumber,
                                    invoiceNumber: 0, // سێرڤەر ژمارەی ڕاستەقینە دادەنێت
                                    remainingAfterPayment,
                                    createdAt: new Date().toISOString(),
                                  });
                                  productsToUpdate.push({
                                    ...product,
                                    paidAmount: product.paidAmount + payAmount,
                                    remainingAmount: remainingAfterPayment,
                                    installments: newInstallmentNumber,
                                  });
                                }
                              }
                              
                              if (productsToUpdate.length > 0) {
                                // پیشاندانی confirmation dialog
                                setPendingPaymentAction({
                                  productsToUpdate,
                                  paymentsToAdd,
                                  withPrint: true,
                                });
                                setShowPaymentConfirmation(true);
                              }
                            }}
                          >
                            <Printer className="h-3 w-3 md:h-4 md:w-4 ml-1 md:mr-2" />
                            <span>پارەدان و چاپکردن</span>
                          </Button>
                              </>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </TabsContent>

            {/* Tab 3: Payment History - Desktop: Table, Mobile: Grouped Cards */}
            <TabsContent value="history" className="mt-0 space-y-3 md:space-y-4">
              {isLoadingPayments ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3"></div>
                  <span className="text-sm">چاوەڕوان بە...</span>
                </div>
              ) : paymentHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                    <History className="h-8 w-8 text-muted-foreground/50" />
                  </div>
                  <span className="text-sm">هیچ پارەدانێک نییە</span>
                </div>
              ) : (
                <>
                  {/* Desktop: Grouped by Product with Expandable Cards */}
                  <div className="hidden md:block" dir="rtl">
                    <div className="flex items-center justify-between pb-3 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <History className="h-4 w-4 text-primary" />
                        </div>
                        <span className="text-sm font-medium">مێژووی پارەدان</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {paymentHistory.length} پارەدان
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      {(() => {
                        const groupedPayments = paymentHistory.reduce((groups, payment) => {
                          const key = payment.productId;
                          if (!groups[key]) {
                            groups[key] = {
                              productId: payment.productId,
                              productName: payment.productName,
                              currency: payment.currency,
                              payments: []
                            };
                          }
                          groups[key].payments.push(payment);
                          return groups;
                        }, {} as Record<string, { productId: string; productName: string; currency: "IQD" | "USD"; payments: PaymentHistory[] }>);

                        return Object.values(groupedPayments).map((group) => {
                          const totalPaid = group.payments.reduce((sum, p) => sum + p.amount, 0);
                          const totalInstallments = group.payments.length;
                          const isExpanded = expandedProducts.has(`history_desktop_${group.productId}`);
                          
                          return (
                            <div 
                              key={group.productId} 
                              className={`rounded-lg border bg-card overflow-hidden transition-all ${
                                isExpanded ? 'border-primary shadow-sm' : ''
                              }`}
                            >
                              {/* Product Header - Click to expand */}
                              <button
                                className={`w-full px-4 py-3 transition-colors cursor-pointer ${
                                  isExpanded ? 'bg-muted/50' : 'hover:bg-muted/30'
                                }`}
                                onClick={() => {
                                  setExpandedProducts(prev => {
                                    const newSet = new Set<string>();
                                    const key = `history_desktop_${group.productId}`;
                                    if (!prev.has(key)) {
                                      newSet.add(key);
                                    }
                                    return newSet;
                                  });
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-md bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                                      <Package className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="text-right">
                                      <p className="font-medium text-sm">{group.productName}</p>
                                      <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-xs text-muted-foreground">{totalInstallments} جار</span>
                                        <span className="text-xs text-muted-foreground">•</span>
                                        <span className="text-xs font-medium text-emerald-600" dir="ltr">
                                          {totalPaid.toLocaleString()} {group.currency === "IQD" ? "د.ع" : "$"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                                    isExpanded ? 'rotate-180' : ''
                                  }`} />
                                </div>
                              </button>
                              
                              {/* Expanded Payment List */}
                              {isExpanded && (
                                <div className="border-t bg-muted/20 p-3">
                                  <div className="space-y-2">
                                    {(() => {
                                      const sortedPayments = [...group.payments].sort((a, b) => b.installmentNumber - a.installmentNumber);
                                      const maxInstallment = Math.max(...group.payments.map(p => p.installmentNumber));
                                      
                                      return sortedPayments.map((payment, paymentIndex) => {
                                        const isLastInstallment = payment.installmentNumber === maxInstallment;
                                        const paymentDateObj = new Date(payment.date);
                                        
                                        return (
                                          <div 
                                            key={payment.id}
                                            className={`flex items-center justify-between bg-card rounded-md px-3 py-2 border ${paymentIndex % 2 === 1 ? 'bg-muted/60' : ''}`}
                                          >
                                            <div className="flex items-center gap-3">
                                              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary font-medium text-xs">
                                                {payment.installmentNumber}
                                              </span>
                                              <div className="flex items-center gap-3 text-sm">
                                                {payment.invoiceNumber ? (
                                                  <span className="text-xs text-amber-600 font-medium">#{payment.invoiceNumber}</span>
                                                ) : null}
                                                <span className="text-muted-foreground text-xs" dir="ltr">
                                                  {paymentDateObj.toLocaleDateString("en-GB")}
                                                </span>
                                              </div>
                                            </div>
                                            
                                            <div className="flex items-center gap-3">
                                              <span className="font-medium text-emerald-600 text-sm" dir="ltr">
                                                {payment.amount.toLocaleString()} {group.currency === "IQD" ? "د.ع" : "$"}
                                              </span>
                                              <div className="flex items-center gap-1">
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-7 w-7 p-0 bg-primary/10 text-primary hover:bg-primary/20"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    printPaymentFromHistory(payment);
                                                  }}
                                                  title="چاپکردن"
                                                >
                                                  <Printer className="h-3.5 w-3.5" />
                                                </Button>
                                                {isLastInstallment && (
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-7 w-7 p-0 bg-destructive/10 text-destructive hover:bg-destructive/20"
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      if (!confirm("دڵنیایت لە سڕینەوەی ئەم پارەدانە؟")) return;
                                                      
                                                      const deleted = await deletePaymentFromDatabase(payment.id);
                                                      if (deleted) {
                                                        const product = products.find(p => p.id === payment.productId);
                                                        if (product) {
                                                          const updatedProduct: Product = {
                                                            ...product,
                                                            paidAmount: product.paidAmount - payment.amount,
                                                            remainingAmount: product.remainingAmount + payment.amount,
                                                            installments: Math.max(0, (product.installments || 0) - 1),
                                                          };
                                                          onUpdateProduct(updatedProduct);
                                                        }
                                                        setPaymentHistory(prev => prev.filter(p => p.id !== payment.id));
                                                      }
                                                    }}
                                                  >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      });
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {/* Mobile: Grouped by Product Cards */}
                  <div className="md:hidden space-y-2" dir="rtl">
                    <div className="flex items-center justify-between pb-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center">
                          <History className="h-3 w-3 text-primary" />
                        </div>
                        <span className="text-xs font-medium">مێژووی پارەدان</span>
                      </div>
                      <Badge variant="secondary" className="text-[10px] h-5">
                        {paymentHistory.length} پارەدان
                      </Badge>
                    </div>
                    
                    {(() => {
                      const groupedPayments = paymentHistory.reduce((groups, payment) => {
                        const key = payment.productId;
                        if (!groups[key]) {
                          groups[key] = {
                            productId: payment.productId,
                            productName: payment.productName,
                            currency: payment.currency,
                            payments: []
                          };
                        }
                        groups[key].payments.push(payment);
                        return groups;
                      }, {} as Record<string, { productId: string; productName: string; currency: "IQD" | "USD"; payments: PaymentHistory[] }>);

                      return Object.values(groupedPayments).map((group) => {
                        const totalPaid = group.payments.reduce((sum, p) => sum + p.amount, 0);
                        const totalInstallments = group.payments.length;
                        const isExpanded = expandedProducts.has(`history_${group.productId}`);
                        
                        return (
                          <div 
                            key={group.productId} 
                            className={`rounded-lg border bg-card overflow-hidden transition-all ${
                              isExpanded ? 'border-primary shadow-sm' : ''
                            }`}
                          >
                            <button
                              className={`w-full p-3 transition-colors cursor-pointer ${
                                isExpanded ? 'bg-muted/50' : 'hover:bg-muted/30'
                              }`}
                              onClick={() => {
                                setExpandedProducts(prev => {
                                  const newSet = new Set<string>();
                                  const key = `history_${group.productId}`;
                                  if (!prev.has(key)) {
                                    newSet.add(key);
                                  }
                                  return newSet;
                                });
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="h-8 w-8 rounded-md bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                                    <Package className="h-4 w-4 text-primary" />
                                  </div>
                                  <div className="text-right">
                                    <p className="font-medium text-xs">{group.productName}</p>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                      <span className="text-[10px] text-muted-foreground">{totalInstallments} جار</span>
                                      <span className="text-[10px] text-muted-foreground">•</span>
                                      <span className="text-[10px] font-medium text-emerald-600" dir="ltr">
                                        {totalPaid.toLocaleString()} {group.currency === "IQD" ? "د.ع" : "$"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                                  isExpanded ? 'rotate-180' : ''
                                }`} />
                              </div>
                            </button>
                            
                            {isExpanded && (
                              <div className="border-t bg-muted/20 p-2">
                                <div className="space-y-1.5">
                                  {(() => {
                                    const sortedPayments = [...group.payments].sort((a, b) => b.installmentNumber - a.installmentNumber);
                                    const maxInstallment = Math.max(...group.payments.map(p => p.installmentNumber));
                                    
                                    return sortedPayments.map((payment, paymentIndex) => {
                                      const isLastInstallment = payment.installmentNumber === maxInstallment;
                                      const paymentDateObj = new Date(payment.date);
                                      
                                      return (
                                        <div 
                                          key={payment.id}
                                          className={`flex items-center justify-between bg-card rounded-md px-2.5 py-2 border ${paymentIndex % 2 === 1 ? 'bg-muted/60' : ''}`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary/10 text-primary font-medium text-[10px]">
                                              {payment.installmentNumber}
                                            </span>
                                            <div className="flex items-center gap-2">
                                              {payment.invoiceNumber ? (
                                                <span className="text-[10px] text-amber-600 font-medium">#{payment.invoiceNumber}</span>
                                              ) : null}
                                              <span className="text-muted-foreground text-[10px]" dir="ltr">
                                                {paymentDateObj.toLocaleDateString("en-GB")}
                                              </span>
                                            </div>
                                          </div>
                                          
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-emerald-600 text-xs" dir="ltr">
                                              {payment.amount.toLocaleString()}
                                            </span>
                                            <div className="flex items-center gap-1.5">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 w-7 p-0 rounded-md bg-primary/10 text-primary hover:bg-primary/20"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  printPaymentFromHistory(payment);
                                                }}
                                              >
                                                <Printer className="h-3.5 w-3.5" />
                                              </Button>
                                              {isLastInstallment && (
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-7 w-7 p-0 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20"
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (!confirm("دڵنیایت لە سڕینەوەی ئەم پارەدانە؟")) return;
                                                    
                                                    const deleted = await deletePaymentFromDatabase(payment.id);
                                                    if (deleted) {
                                                      const product = products.find(p => p.id === payment.productId);
                                                      if (product) {
                                                        const updatedProduct: Product = {
                                                          ...product,
                                                          paidAmount: product.paidAmount - payment.amount,
                                                          remainingAmount: product.remainingAmount + payment.amount,
                                                          installments: Math.max(0, (product.installments || 0) - 1),
                                                        };
                                                        onUpdateProduct(updatedProduct);
                                                      }
                                                      setPaymentHistory(prev => prev.filter(p => p.id !== payment.id));
                                                    }
                                                  }}
                                                >
                                                  <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Tab 4: Completed - Table Format */}
            <TabsContent value="completed" className="mt-0 space-y-3 md:space-y-4">
              <CardDescription className="text-xs md:text-sm">{completedProducts.length} کاڵای تەواوبوو</CardDescription>
              {completedProducts.length === 0 ? (
                <div className="text-center py-8 md:py-12 text-muted-foreground border rounded-lg text-sm">
                  هیچ کاڵایەکی تەواوبوو نییە
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="rounded-md border hidden md:block" dir="rtl">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right w-12">#</TableHead>
                          <TableHead className="text-right">ناوی ئامێر</TableHead>
                          <TableHead className="text-right">نرخی فرۆشتن</TableHead>
                          <TableHead className="text-right">وەرگیراو</TableHead>
                          <TableHead className="text-right">بەرواری کڕین</TableHead>
                          <TableHead className="text-right w-fit"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {completedProducts.map((product, index) => (
                          <TableRow key={product.id} className={index % 2 === 1 ? 'bg-muted/50' : ''}>
                            <TableCell className="text-center font-medium text-muted-foreground">
                              {index + 1}
                            </TableCell>
                            <TableCell>
                              <div>
                                <span className="font-medium">{product.deviceName}</span>
                                {product.deviceDescription && (
                                  <p className="text-sm text-muted-foreground">{product.deviceDescription}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell dir="ltr" className="text-right">
                              {product.salePrice.toLocaleString()}{" "}
                              <Badge variant="outline" className="mr-1">
                                {product.currency === "IQD" ? "د.ع" : "$"}
                              </Badge>
                            </TableCell>
                            <TableCell dir="ltr" className="text-right text-green-600">
                              {product.paidAmount.toLocaleString()}{" "}
                              <Badge variant="secondary" className="mr-1">
                                {product.currency === "IQD" ? "د.ع" : "$"}
                              </Badge>
                            </TableCell>
                            <TableCell dir="ltr" className="text-right">{product.date}</TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                                  <MoreHorizontal className="h-4 w-4" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="text-right w-48">
                                  <EditProductDialog product={product} onUpdate={onUpdateProduct} />
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onSelect={() => {
                                      if (confirm("دڵنیایت لە سڕینەوەی ئەم کاڵایە؟")) {
                                        onDeleteProduct(product.id);
                                      }
                                    }}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 ml-2" />
                                    سڕینەوە
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-3">
                    {completedProducts.map((product, index) => {
                      const isExpanded = expandedCompletedProducts.has(product.id);
                      const isEven = index % 2 === 1;
                      return (
                        <div key={product.id} className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}>
                          <div className="p-3 cursor-pointer" onClick={() => toggleCompletedProductExpanded(product.id)}>
                            {/* Main Row */}
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm truncate">{product.deviceName}</span>
                                  <Badge variant={product.currency === "IQD" ? "outline" : "secondary"} className="text-[10px] px-1.5 h-4">
                                    {product.currency === "IQD" ? "د.ع" : "$"}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5" dir="ltr">
                                  {product.date}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCompletedProductExpanded(product.id);
                                  }}
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger onClick={(e) => e.stopPropagation()} className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="text-right w-48">
                                    <EditProductDialog product={product} onUpdate={onUpdateProduct} />
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onSelect={() => {
                                        if (confirm("دڵنیایت لە سڕینەوەی ئەم کاڵایە؟")) {
                                          onDeleteProduct(product.id);
                                        }
                                      }}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 ml-2" />
                                      سڕینەوە
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                            {/* Mini Table */}
                            <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                              <div className="text-center">
                                <div className="text-muted-foreground text-[10px]">نرخ</div>
                                <div className="font-semibold" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.salePrice.toLocaleString()}</>}</div>
                              </div>
                              <div className="text-center border-x border-dashed">
                                <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                <div className="font-semibold text-green-600" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-muted-foreground text-[10px]">دۆخ</div>
                                <Badge variant="default" className="text-[10px] px-1.5 h-4 bg-green-600">تەواو</Badge>
                              </div>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                              <div className="space-y-2 pt-3">
                                <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                  <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                                  <span className="font-medium text-sm" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.monthlyInstallment.toLocaleString()}</>}</span>
                                </div>
                                <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                  <span className="text-muted-foreground text-xs">ژمارەی قیستەکان</span>
                                  <span className="font-medium text-sm">{product.installments || 0}</span>
                                </div>
                                {product.deviceDescription && (
                                  <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                    <span className="text-muted-foreground text-xs">وەسف</span>
                                    <span className="font-medium text-sm">{product.deviceDescription}</span>
                                  </div>
                                )}
                                {product.notes && (
                                  <div className="pt-2 mt-1 border-t">
                                    <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                                    <p className="text-sm bg-muted/50 rounded-lg p-2 text-muted-foreground">{product.notes}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>

    {/* Payment Confirmation Dialog */}
    {showPaymentConfirmation && pendingPaymentAction && (
      <PaymentConfirmationDialog
        open={showPaymentConfirmation}
        onOpenChange={setShowPaymentConfirmation}
        onConfirm={handleConfirmPayment}
        totalAmount={pendingPaymentAction.paymentsToAdd.reduce((sum, p) => sum + p.amount, 0)}
        currency={paymentCurrency}
        productCount={pendingPaymentAction.productsToUpdate.length}
        date={globalPaymentDate}
        withPrint={pendingPaymentAction.withPrint}
      />
    )}

    {/* پسوڵەی چاپکردن - شاراوە لە ڕووکار، تەنها بۆ چاپکردن */}
    {invoiceData && customer && (
      <div className="fixed left-[-9999px] top-0">
        <PaymentInvoice
          ref={invoiceRef}
          customer={customer}
          payments={invoiceData.payments}
          products={products}
          invoiceNumber={invoiceData.invoiceNumber}
          paymentDate={invoiceData.paymentDate}
        />
      </div>
    )}
    </div>
  );
}
